import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight, Webhook, CheckCircle2, XCircle, AlertCircle, RefreshCw, Copy, AlertTriangle, ShieldAlert } from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { formatDateTimeAU } from "@/lib/formatDateAU";
import { toast } from "sonner";

const STATUS_STY = {
  received:             { bg: "bg-slate-100",   text: "text-slate-700",   icon: AlertCircle,  label: "Received" },
  processed:            { bg: "bg-emerald-50",  text: "text-emerald-700", icon: CheckCircle2, label: "Processed" },
  duplicate:            { bg: "bg-amber-50",    text: "text-amber-700",   icon: RefreshCw,    label: "Duplicate" },
  processing_failed:    { bg: "bg-rose-50",     text: "text-rose-700",    icon: XCircle,      label: "Processing failed" },
  verification_failed:  { bg: "bg-rose-50",     text: "text-rose-700",    icon: XCircle,      label: "Verification failed" },
};

export default function AdminStripeLogs() {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [orphans, setOrphans] = useState([]);
  const [orphansOpenCount, setOrphansOpenCount] = useState(0);
  const [orphansExpanded, setOrphansExpanded] = useState(false);
  const [resolvingId, setResolvingId] = useState(null);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (filter !== "all") qs.set("status_filter", filter);
    api.get(`/admin/stripe/events?${qs}`).then(({ data }) => setEvents(data)).finally(() => setLoading(false));
  }, [filter]);

  const loadOrphans = () => {
    api.get("/admin/payments/orphans?resolved=false").then(({ data }) => {
      setOrphans(data.orphans || []);
      setOrphansOpenCount(data.open_count || 0);
    }).catch(() => {});
  };
  useEffect(() => { loadOrphans(); }, []);

  const resolveOrphan = async (id) => {
    setResolvingId(id);
    try {
      await api.post(`/admin/payments/orphans/${id}/resolve`, { note: "Acknowledged by admin" });
      toast.success("Orphan resolved");
      loadOrphans();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setResolvingId(null);
    }
  };

  const visible = events.filter((e) =>
    !query || (e.event_type || "").toLowerCase().includes(query.toLowerCase()) ||
    (e.stripe_event_id || "").toLowerCase().includes(query.toLowerCase()),
  );

  const counts = events.reduce((a, e) => ({ ...a, [e.status]: (a[e.status] || 0) + 1 }), {});

  return (
    <PortalShell title="Stripe webhook logs" subtitle="Admin · System">
      <div className="mb-5 rounded-2xl bg-slate-900 text-white p-5 flex items-start gap-4">
        <Webhook className="w-5 h-5 text-cyan-400 mt-0.5" />
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-wider text-cyan-400 font-bold">Production webhook</div>
          <div className="text-sm mt-1">Endpoint: <code className="bg-white/10 px-1.5 py-0.5 rounded font-mono text-xs">/api/webhook/stripe</code></div>
          <p className="text-xs text-slate-300 mt-1.5">Signature verification is enforced. Requests without a valid <code className="bg-white/10 px-1 rounded">Stripe-Signature</code> header are rejected with HTTP 400 and logged below. Duplicate events (same <code className="bg-white/10 px-1 rounded">stripe_event_id</code> already processed) are tagged but not reprocessed.</p>
        </div>
      </div>

      {/* Phase 2L-A54 — Orphan-event alarm. High-priority surface for
          paid Stripe events that couldn't be matched to a local booking.
          Hidden when zero (don't spam admins with empty state). */}
      {orphansOpenCount > 0 && (
        <div
          data-testid="payment-orphan-alarm"
          className="mb-5 rounded-2xl border-2 border-rose-400 bg-rose-50 p-5"
        >
          <div className="flex items-start gap-3">
            <ShieldAlert className="w-6 h-6 text-rose-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-rose-700 font-bold">High-priority alarm</div>
                  <div className="text-sm font-semibold text-rose-900 mt-0.5">
                    <span data-testid="payment-orphan-count">{orphansOpenCount}</span> paid Stripe event{orphansOpenCount === 1 ? "" : "s"} arrived without a matching local booking
                  </div>
                </div>
                <Button
                  data-testid="payment-orphan-toggle"
                  size="sm"
                  variant="outline"
                  onClick={() => setOrphansExpanded((x) => !x)}
                  className="border-rose-300 text-rose-800 hover:bg-rose-100"
                >
                  {orphansExpanded ? "Hide" : "Show"} details
                </Button>
              </div>
              <p className="text-xs text-rose-800 mt-2">
                This usually means Stripe is delivering webhooks to the wrong environment, or booking metadata was stripped before the session was created. Investigate the events listed below and reconcile from <Link to="/admin/bookings" className="underline font-semibold">Bookings → Reconcile from Stripe</Link>.
              </p>
              {orphansExpanded && (
                <div className="mt-4 rounded-xl bg-white border border-rose-200 overflow-hidden" data-testid="payment-orphan-list">
                  <table className="w-full text-xs">
                    <thead className="bg-rose-50/60">
                      <tr>
                        <th className="text-left p-2 text-[10px] uppercase tracking-wider font-bold text-rose-700">Received</th>
                        <th className="text-left p-2 text-[10px] uppercase tracking-wider font-bold text-rose-700">Kind</th>
                        <th className="text-left p-2 text-[10px] uppercase tracking-wider font-bold text-rose-700">Reason</th>
                        <th className="text-left p-2 text-[10px] uppercase tracking-wider font-bold text-rose-700">Stripe id</th>
                        <th className="text-left p-2 text-[10px] uppercase tracking-wider font-bold text-rose-700">Customer</th>
                        <th className="text-left p-2 text-[10px] uppercase tracking-wider font-bold text-rose-700">Ref hint</th>
                        <th className="text-left p-2 text-[10px] uppercase tracking-wider font-bold text-rose-700">Amount</th>
                        <th className="text-right p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {orphans.map((o) => (
                        <tr key={o.id} className="border-t border-rose-100" data-testid={`payment-orphan-row-${o.id}`}>
                          <td className="p-2 whitespace-nowrap text-slate-600">{formatDateTimeAU(o.received_at)}</td>
                          <td className="p-2 font-mono text-[11px]">{o.kind}</td>
                          <td className="p-2">{o.reason}</td>
                          <td className="p-2 font-mono text-[10px] text-slate-600 max-w-[140px] truncate" title={o.stripe_id || ""}>{o.stripe_id || "—"}</td>
                          <td className="p-2">{o.customer_email || "—"}</td>
                          <td className="p-2 font-mono">{o.metadata_reference || "—"}</td>
                          <td className="p-2">${(o.amount || 0).toFixed?.(2) ?? o.amount}</td>
                          <td className="p-2 text-right">
                            <Button
                              size="sm" variant="outline"
                              data-testid={`payment-orphan-resolve-${o.id}`}
                              disabled={resolvingId === o.id}
                              onClick={() => resolveOrphan(o.id)}
                              className="h-7 border-rose-300 text-rose-800 hover:bg-rose-100"
                            >
                              {resolvingId === o.id ? "Resolving…" : "Resolve"}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        {["all","received","processed","duplicate","processing_failed","verification_failed"].map((s) => {
          const m = s === "all" ? { label: "All", bg: "bg-slate-100", text: "text-slate-700" } : STATUS_STY[s];
          const active = filter === s;
          return (
            <button key={s} onClick={() => setFilter(s)} data-testid={`stripe-filter-${s}`}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 ${active ? "bg-slate-900 text-white" : `${m.bg} ${m.text}`}`}>
              {m.label}
              <span className={`px-1.5 py-0.5 rounded text-[10px] ${active ? "bg-white/20" : "bg-white/60"}`}>{s === "all" ? events.length : (counts[s] || 0)}</span>
            </button>
          );
        })}
      </div>

      <div className="relative max-w-md mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input data-testid="stripe-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter by event type or event id" className="pl-10 h-11 rounded-full bg-white" />
      </div>

      <div data-testid="stripe-events-table" className="rounded-3xl bg-white border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading events…</div>
        ) : visible.length === 0 ? (
          <div className="p-10 text-center text-slate-500">No webhook events yet. They&apos;ll appear here as soon as Stripe starts firing.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Received</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Event type</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Stripe event id</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Status</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Result / Error</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((e) => {
                  const m = STATUS_STY[e.status] || STATUS_STY.received;
                  const Icon = m.icon;
                  return (
                    <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                      <td className="p-4 text-xs text-slate-500 whitespace-nowrap">{formatDateTimeAU(e.received_at)}</td>
                      <td className="p-4 font-mono text-xs text-slate-700">{e.event_type || "—"}</td>
                      <td className="p-4 font-mono text-xs text-slate-500 group cursor-pointer" onClick={() => e.stripe_event_id && navigator.clipboard.writeText(e.stripe_event_id)} title="Click to copy">
                        <span className="inline-flex items-center gap-1.5">{e.stripe_event_id || "—"}{e.stripe_event_id && <Copy className="w-3 h-3 opacity-0 group-hover:opacity-60" />}</span>
                      </td>
                      <td className="p-4"><span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${m.bg} ${m.text}`}><Icon className="w-3 h-3" />{m.label}</span></td>
                      <td className="p-4 text-xs text-slate-600 max-w-[360px]" title={e.processing_result || e.error || ""}>{e.processing_result || e.error || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </PortalShell>
  );
}
